import { conditionalRender } from '@repo/toolkit/conditional-render'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getOptions } from '../../../api/options/get-options'
import { getPeriods } from '../../../api/periods/get-periods'
import { getTrips } from '../../../api/trips/get-trips'
import { ProductFilters } from '../../../lib/types/models/product/product'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { yearsFilterOptions } from '../../../views/pcr-personal-space/utils'

interface Props {
  filter?: ProductFilters
  onFilterChange: (filter: ProductFilters) => void
}

export function ProductsFilter({ filter, onFilterChange }: Props) {
  function setFilter<K extends keyof ProductFilters>(key: K, value: ProductFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: journeyPlaces } = useQuery({
    queryKey: ['journey-places'],
    queryFn: () => getJourneyPlaces()
  })

  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: () => getOptions()
  })

  const { data: trips } = useQuery({
    queryKey: ['trips'],
    queryFn: () => getTrips()
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  return (
    <div className={'flex flex-col gap-3'}>
      <div />

      <MultiSelectInput<number>
        label={'Année'}
        variant={'primary'}
        options={yearsFilterOptions}
        selectedOptions={filter?.years?.map((year) => ({
          label: yearsFilterOptions.find((y) => y.value === year)?.label ?? '',
          value: year
        }))}
        onChange={(years) => setFilter('years', years)}
      />

      <MultiSelectInput
        searchable
        label={'Périodes'}
        variant={'primary'}
        options={periods?.data.map((period) => ({ label: period.name, value: period.name })) ?? []}
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data.find((p) => p.name === period)?.name ?? '',
          value: period
        }))}
        onChange={(periods) => setFilter('periods', periods)}
      />

      <Input
        type={'number'}
        placeholder={'Durée en jours'}
        placeholderVariant={'dark'}
        value={filter?.durationInDays?.[0]}
        errorMessage={conditionalRender(
          filter?.durationInDays && filter.durationInDays[0] < 0 ? true : false,
          'La durée doit être supérieur à zéro'
        )}
        onChange={(e) =>
          setFilter('durationInDays', +e.target.value === 0 ? undefined : [+e.target.value])
        }
      />

      <MultiSelectInput
        label={'Lieux'}
        searchable
        variant={'primary'}
        options={
          journeyPlaces?.data.map((journeyPlace) => ({
            label: journeyPlace.name,
            value: journeyPlace.name
          })) ?? []
        }
        selectedOptions={filter?.journeyPlaces?.map((journeyPlace) => ({
          label: journeyPlace,
          value: journeyPlace
        }))}
        onChange={(journeyPlaces) => setFilter('journeyPlaces', journeyPlaces)}
      />

      <MultiSelectInput
        label={'Thème'}
        variant={'primary'}
        searchable
        options={
          options?.data.map((mainOption) => ({
            label: getTranslatedName(mainOption),
            value: getTranslatedName(mainOption)
          })) ?? []
        }
        selectedOptions={filter?.mainOptions?.map((mainOption) => ({
          label: mainOption,
          value: mainOption
        }))}
        onChange={(mainOptions) => setFilter('mainOptions', mainOptions)}
      />

      <MultiSelectInput
        label={'Options'}
        searchable
        variant={'primary'}
        options={
          options?.data.map((option) => ({
            label: getTranslatedName(option),
            value: getTranslatedName(option)
          })) ?? []
        }
        selectedOptions={filter?.options?.map((option) => ({
          label: option,
          value: option
        }))}
        onChange={(options) => setFilter('options', options)}
      />

      <MultiSelectInput
        label={'Voyages'}
        searchable
        variant={'primary'}
        options={
          trips?.data.map((option) => ({
            label: TripProperties.getTripLabel(option),
            value: option.id
          })) ?? []
        }
        selectedOptions={filter?.tripIds?.map((tripId) => ({
          label: TripProperties.getTripLabel(trips?.data.find((t) => t.id === tripId)),
          value: tripId
        }))}
        onChange={(tripIds) => setFilter('tripIds', tripIds)}
      />

      <SelectInput
        label={'Pays'}
        options={countries?.data.map((country) => ({ label: country.name, value: country.id }))}
        selectedOption={conditionalRender(!!filter?.countryId, {
          label: countries?.data.find((country) => country.id === filter?.countryId)?.name ?? '',
          value: filter?.countryId
        })}
        onChange={(id) => setFilter('countryId', id)}
      />

      <SelectInput
        label={'Formule'}
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: formula.id
        }))}
        selectedOption={conditionalRender(!!filter?.formulaId, {
          label: getTranslatedName(
            formulas?.data.find((formula) => formula.id === filter?.formulaId)
          ),
          value: filter?.formulaId
        })}
        onChange={(id) => setFilter('formulaId', id)}
      />
    </div>
  )
}
